import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ResourceType } from '@ds-frontend/resource'
import { Rate } from 'antd'
import cx from 'classnames'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import PropTypes from 'prop-types'

import CollapsibleParagraph from './CollapsibleParagraph'
import API from 'api'
import { useTranslations } from 'common/language'
import formatPrice from 'common/utils/formatPrice'
import { BlockSectionTitle, Button, Container, CTALink, Typography } from 'common/widgets'
import SchoolRating from 'components/schoolFinder/FinderList/widgets/SchoolRating'
import classNames from './styles.module.scss'


ProductReviews.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string,
    heading_hx: PropTypes.string,
    kind: PropTypes.string.isRequired,
    parent_id: PropTypes.string.isRequired,
    rating: PropTypes.number.isRequired,
    reviews_count: PropTypes.number.isRequired,
    scores: PropTypes.object,
  }).isRequired,
  productReviews: ResourceType.isRequired,
}

export default function ProductReviews({
  data,
  productReviews,
}) {
  const { gettext, ngettext } = useTranslations()
  const rateRef = useRef(null)
  const reviewsContainer = useRef(null)
  const [reviews, setReviews] = useState(get(productReviews, 'data.results', []))
  const [offset, setOffset] = useState(3)
  const [loading, setLoading] = useState(false)
  const textLabels = {
    course_quality: gettext('Course quality'),
    leisure_program: gettext('Leisure program'),
    location: gettext('Location'),
    infrastructure: gettext('Infrastructure'),
    atmosphere: gettext('Atmosphere'),
  }
  const isMoreEnabled = useMemo(() => reviews.length < get(productReviews, 'data.count'), [reviews, productReviews?.data])
  const getAdditionalExperiences = useCallback(() => {
    setLoading(true)
    API.get('api/v1/feefo/product-reviews', { params: { kind: data.kind, parent_id: data.parent_id, offset: offset, limit: 3 } })
      .then(res => {
        setOffset(offset => offset + 3)
        setReviews(r => r.concat(res.results))
      })
      .finally(_ => setLoading(false))
  }, [data.kind, data.parent_id, offset])
  const scores = Object.entries(data.scores || {}).map(([key, value]) => ({ text: textLabels[key], value }))

  useEffect(() => {
    // This effect is required to clear the lighthouse error:
    //  "List items (<li>) are not contained within <ul>, <ol> or <menu> parent elements."
    // This happens because Rate component which implements 5-star rating has layout with:
    // <ul role="radiogroup">...
    // Because of this the <li> elements are wrongly flagged by Lighthouse as not inside the <ul>.
    if(rateRef?.current?.rate?.role && reviewsContainer?.current) {
      rateRef.current.rate.role = undefined
      Array.from(reviewsContainer.current.getElementsByTagName('ul')).forEach(ul => {
        ul.role = undefined
      })
    }
  }, [])

  return (
    <div className={classNames.wrapper}>
      <Container size="xl">
        <BlockSectionTitle heading={data.heading} headingHx={data.heading_hx} />
        {!isEmpty(reviews) ? (
          <Fragment>
            <div>
              <span className={classNames.score}>
                {data.rating} / 5
              </span>
              <Rate ref={rateRef} disabled allowHalf defaultValue={data.rating} className={cx(classNames.star, classNames.starColor)} />
              <span className={classNames.count}>
                {formatPrice(data.reviews_count)} {ngettext('Review', 'Reviews', data.reviews_count)}
              </span>
            </div>
            {data.kind === 'school' && (
              <div className={classNames.schoolRatings}>
                <SchoolRating ratings={scores} large />
              </div>
            )}
            <div ref={reviewsContainer}>
              {reviews.map((item, index) => (
                <div key={index} className={classNames.reviewCard}>
                  <div>
                    <span>{item.author_display_name || gettext('Anonymous')} | {data.kind !== 'school' ? <CTALink cta={item.school_name} ctaURL={item.school_url} className={classNames.link} /> : item.school_name}, {item.destination_name}</span>
                  </div>
                  <div>
                    <Rate disabled allowHalf defaultValue={item.rating} className={cx(classNames.rating, classNames.starColor)} />
                    <span>{item.period}</span>
                  </div>
                  {item.text && <CollapsibleParagraph className={cx(classNames.reviewText, classNames.wrap)} text={item.text} />}
                  {item.answer_text && <blockquote className={classNames.answerText}>
                    <p>
                      {gettext('Answer from Boa Lingua')}
                    </p>
                    <CollapsibleParagraph text={item.answer_text} className={classNames.wrap} />
                  </blockquote>}
                </div>
              ))}
            </div>
            {isMoreEnabled && (
              <div className={classNames.showMore}>
                <Button
                  onClick={getAdditionalExperiences}
                  className={classNames.button}
                  isLoading={loading}
                >
                  {gettext('more')}
                </Button>
              </div>)}
          </Fragment>
        ) : (
          <Typography
            variant="h4"
            align="center"
            className={classNames.emptyText}
          >
            {gettext('There is no review available yet.')}
          </Typography>
        )}
      </Container>
    </div>
  )
}
